import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  assessmentsService,
  FinalReportDelivery as DeliveryRecord,
} from '../api/assessmentsService';

const errorMessage = (err: any, fallback: string) =>
  err?.response?.data?.message || err?.message || fallback;

const formatDateTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const EmailModal: React.FC<{
  assessmentId: number;
  participantId: number;
  candidateLabel: string;
  onClose: () => void;
  onSent: () => void;
}> = ({ assessmentId, participantId, candidateLabel, onClose, onSent }) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ sentCount: number; failedCount: number; data: DeliveryRecord[] } | null>(null);

  const recipientsQuery = useQuery({
    queryKey: ['assessments', assessmentId, 'final-reports', participantId, 'recipients'],
    queryFn: () => assessmentsService.getFinalReportRecipients(assessmentId, participantId),
  });

  const recipients = recipientsQuery.data?.data ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sendMutation = useMutation({
    mutationFn: () =>
      assessmentsService.sendFinalReportEmail(assessmentId, participantId, {
        userIds: Array.from(selectedIds),
        note: note.trim() || undefined,
      }),
    onSuccess: (response) => {
      setError(null);
      setResult({
        sentCount: response.sentCount,
        failedCount: response.failedCount,
        data: response.data,
      });
      onSent();
    },
    onError: (err) => {
      setResult(null);
      setError(errorMessage(err, 'Could not send the report.'));
    },
  });

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = () => {
    const count = selectedIds.size;
    if (!count) return;
    const ok = window.confirm(
      `Email the report for ${candidateLabel} to ${count} hub leader${count === 1 ? '' : 's'}?`,
    );
    if (!ok) return;
    sendMutation.mutate();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-[101] flex max-h-[88vh] w-full max-w-lg flex-col rounded-xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-base font-semibold text-gray-900">Email report to hub leaders</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {candidateLabel} · sends the report as a PDF attachment
          </p>
        </div>

        <div className="space-y-4 overflow-y-auto px-4 py-4">
          {result ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                Sent to {result.sentCount} hub leader{result.sentCount === 1 ? '' : 's'}.
              </div>
              {result.data.filter((d) => d.status === 'failed').map((delivery) => (
                <div
                  key={delivery.id}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  <span className="font-medium">{delivery.recipientName || delivery.recipientEmail}</span>
                  <span className="block">{delivery.error || 'Could not be delivered.'}</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              {recipientsQuery.isLoading && <p className="text-sm text-gray-600">Loading hub leaders…</p>}
              {recipientsQuery.isError && (
                <p className="text-sm text-red-700">Could not load hub leaders.</p>
              )}
              {!recipientsQuery.isLoading && recipients.length === 0 && (
                <p className="text-sm text-gray-600">No active hub leaders to send to.</p>
              )}

              <ul className="space-y-1">
                {recipients.map((recipient) => (
                  <li
                    key={recipient.id}
                    className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      checked={selectedIds.has(recipient.id)}
                      onChange={() => toggle(recipient.id)}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {`${recipient.firstName || ''} ${recipient.lastName || ''}`.trim() || recipient.email}
                      </div>
                      <div className="truncate text-xs text-gray-500">{recipient.email}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Note (optional)</label>
                <textarea
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything the hub leader should know before reading the report…"
                />
              </div>

              {error && <p className="text-sm text-red-700">{error}</p>}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleSend}
              disabled={selectedIds.size === 0 || sendMutation.isPending}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
            >
              {sendMutation.isPending
                ? 'Sending…'
                : `Send to ${selectedIds.size} hub leader${selectedIds.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Delivery controls for the final report. Every send is an explicit action:
 * nothing is emailed or published to a candidate automatically.
 */
const FinalReportDelivery: React.FC<{
  assessmentId: number;
  participantId: number;
  candidateLabel: string;
  publishedAt?: string | null;
  deliveries: DeliveryRecord[];
  canEdit: boolean;
  onChanged: () => void;
}> = ({ assessmentId, participantId, candidateLabel, publishedAt, deliveries, canEdit, onChanged }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => assessmentsService.getFinalReportPdf(assessmentId, participantId),
    onSuccess: (blob) => {
      setError(null);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoked on a delay so the new tab has time to load the blob.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (err) => setError(errorMessage(err, 'Could not build the PDF.')),
  });

  const publishMutation = useMutation({
    mutationFn: () => assessmentsService.publishFinalReport(assessmentId, participantId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => setError(errorMessage(err, 'Could not publish the report.')),
  });

  const unpublishMutation = useMutation({
    mutationFn: () => assessmentsService.unpublishFinalReport(assessmentId, participantId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => setError(errorMessage(err, 'Could not unpublish the report.')),
  });

  const emailDeliveries = useMemo(
    () => deliveries.filter((delivery) => delivery.channel === 'email'),
    [deliveries],
  );

  const handlePublish = () => {
    const ok = window.confirm(
      `Publish this report to ${candidateLabel} in the plant portal? They will be able to read it immediately.`,
    );
    if (!ok) return;
    publishMutation.mutate();
  };

  const handleUnpublish = () => {
    if (!window.confirm('Hide this report from the candidate again?')) return;
    unpublishMutation.mutate();
  };

  return (
    <div className="space-y-4 rounded-xl bg-white p-4 shadow-lg sm:p-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Deliver</h2>
        <p className="mt-1 text-sm text-gray-600">
          Reports are only ever sent by hand. Nothing here goes out on its own.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          onClick={() => previewMutation.mutate()}
          disabled={previewMutation.isPending}
        >
          {previewMutation.isPending ? 'Building PDF…' : 'Preview PDF'}
        </button>

        {canEdit && (
          <button
            type="button"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            onClick={() => setShowEmailModal(true)}
          >
            Email to hub leaders…
          </button>
        )}

        {canEdit && (publishedAt ? (
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            onClick={handleUnpublish}
            disabled={unpublishMutation.isPending}
          >
            Unpublish from candidate
          </button>
        ) : (
          <button
            type="button"
            className="rounded-md border border-primary-300 bg-white px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-60"
            onClick={handlePublish}
            disabled={publishMutation.isPending}
          >
            Publish to candidate…
          </button>
        ))}
      </div>

      <div
        className={[
          'rounded-lg border px-4 py-3 text-sm',
          publishedAt ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-gray-200 bg-gray-50 text-gray-700',
        ].join(' ')}
      >
        {publishedAt
          ? `Visible to the candidate in the plant portal since ${formatDateTime(publishedAt)}.`
          : 'Not visible to the candidate. Publishing is the only way they can see it.'}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900">Delivery history</h3>
        {deliveries.length === 0 ? (
          <p className="mt-1 text-sm text-gray-600">Nothing has been sent yet.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {deliveries.map((delivery) => (
              <li key={delivery.id} className="flex flex-wrap items-baseline gap-x-2 text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {delivery.channel === 'email'
                    ? `Emailed ${delivery.recipientName || delivery.recipientEmail || 'hub leader'}`
                    : delivery.status === 'revoked'
                      ? 'Unpublished from candidate'
                      : 'Published to candidate'}
                </span>
                <span>{formatDateTime(delivery.sentAt)}</span>
                {delivery.sentByUser && (
                  <span>
                    by {`${delivery.sentByUser.firstName || ''} ${delivery.sentByUser.lastName || ''}`.trim()}
                  </span>
                )}
                {delivery.status === 'failed' && (
                  <span className="text-red-700">failed — {delivery.error || 'unknown error'}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {emailDeliveries.length > 0 && (
          <p className="mt-2 text-[11px] text-gray-500">
            {emailDeliveries.filter((d) => d.status === 'sent').length} successful email
            {emailDeliveries.filter((d) => d.status === 'sent').length === 1 ? '' : 's'} so far.
          </p>
        )}
      </div>

      {showEmailModal && (
        <EmailModal
          assessmentId={assessmentId}
          participantId={participantId}
          candidateLabel={candidateLabel}
          onClose={() => setShowEmailModal(false)}
          onSent={onChanged}
        />
      )}
    </div>
  );
};

export default FinalReportDelivery;
